import type { SchemeKey } from '@/stores/colorSchemes'

/**
 * Interface appearance preferences.
 *
 * This key is the browser's first-paint cache: `readCached` stays synchronous
 * so the look applies before the first paint (index.html reads the same key).
 * The account copy lives on the server as the `admin.appearance` settings
 * document, kept in step by SettingsSyncStore.
 */

/** Five families with full Cyrillic (Russian, Uzbek Cyrillic) in every weight the UI uses. */
export type FontKey = 'geist' | 'inter' | 'golos' | 'onest' | 'manrope'

export type AppearanceSettings = {
  // Style
  /** A complete palette (canvas, panels, text, lines, buttons, table headers); see colorSchemes. */
  scheme: SchemeKey
  accent: string
  font: FontKey
  textSize: 'sm' | 'md' | 'lg' | 'xl'
  radius: 'sharp' | 'default' | 'soft'
  spacing: 'compact' | 'default' | 'relaxed'
  /** Size of buttons, fields, badges and statuses, alerts and switchers. */
  controlSize: 'xs' | 'sm' | 'md' | 'lg'
  background: 'ambient' | 'plain' | 'warm' | 'cool'
  glass: boolean
  motion: 'full' | 'reduced'
  // Layout
  /** Where the main menu sits: a side column, or three rows along the top. */
  sidebarSide: 'left' | 'right' | 'top'
  /** Top menu fill: '' keeps the glass rows; a colour fills row 1 with it and row 2 with its light tint. */
  navFill: string
  contentWidth: 'full' | 'limited'
  zebra: boolean
  /** Digits in tables and figures: interface font with even columns, interface font as is, or monospace. */
  numbers: 'tabular' | 'proportional' | 'mono'
  // Windows and notifications
  editorMode: 'auto' | 'drawer' | 'modal'
  drawerSide: 'right' | 'left'
  drawerWidth: 'narrow' | 'standard' | 'wide'
  backdrop: 'blur' | 'dim' | 'none'
  outsideClose: boolean
  toastPosition: 'bottom-right' | 'top-right' | 'bottom-center' | 'top-center'
}

/** What <html> needs, precomputed so the boot script applies it without logic. */
export type AppliedAppearance = {
  vars: Record<string, string>
  attrs: Record<string, string>
}

type StoredAppearance = { settings: AppearanceSettings; applied: AppliedAppearance }

export const APPEARANCE_STORAGE_KEY = 'mf.ui.appearance'

export const appearanceService = {
  readCached(): Partial<AppearanceSettings> | null {
    try {
      const raw = localStorage.getItem(APPEARANCE_STORAGE_KEY)
      return raw ? (JSON.parse(raw) as StoredAppearance).settings ?? null : null
    } catch {
      return null
    }
  },

  async save(settings: AppearanceSettings, applied: AppliedAppearance): Promise<void> {
    try {
      localStorage.setItem(APPEARANCE_STORAGE_KEY, JSON.stringify({ settings, applied } satisfies StoredAppearance))
    } catch {
      /* storage unavailable: the choice still applies for this session */
    }
  },
}
